import { type FormEvent, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { i18n } from '@wms/shared';
import { ApiError, api } from '../lib/api.js';
import { Dialog } from './Dialog.js';
import { NumberInput } from './NumberInput.js';
import { formatQty } from '../lib/format.js';

const { t } = i18n;

interface ReceiptPallet {
  id: string;
  code: string;
}
interface ShelfItem {
  skuId: string;
  code: string;
  nameLt: string;
  partnerId: string;
  shelfQty: number;
}
interface TransferResult {
  palletId: string;
  palletCode: string;
  created: boolean;
  qty: number;
}

export function MoveShelfToPalletDialog({
  item,
  onClose,
  onMoved,
}: {
  item: ShelfItem;
  onClose: () => void;
  onMoved: () => void;
}) {
  const [pallets, setPallets] = useState<ReceiptPallet[]>([]);
  const [target, setTarget] = useState('NEW');
  const [qty, setQty] = useState(item.shelfQty);
  const [reason, setReason] = useState('');
  const [locationRow, setLocationRow] = useState('');
  const [locationShelf, setLocationShelf] = useState('');
  const [locationPosition, setLocationPosition] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<TransferResult | null>(null);

  useEffect(() => {
    api<ReceiptPallet[]>(`/partners/${item.partnerId}/stock/receipt-pallets`)
      .then(setPallets)
      .catch(() => setError(t('common.error')));
  }, [item.partnerId]);

  const valid =
    Number.isInteger(qty) && qty > 0 && qty <= item.shelfQty && reason.trim().length > 0;

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!valid || busy) return;
    setBusy(true);
    setError(null);
    try {
      const moved = await api<TransferResult>('/pallets/from-shelf', {
        method: 'POST',
        body: {
          partnerId: item.partnerId,
          skuId: item.skuId,
          qty,
          reason: reason.trim(),
          target:
            target === 'NEW'
              ? {
                  type: 'NEW',
                  locationRow: locationRow.trim(),
                  locationShelf: locationShelf.trim(),
                  locationPosition: locationPosition.trim(),
                }
              : { type: 'EXISTING', palletId: target },
        },
      });
      setResult(moved);
      onMoved();
    } catch (err) {
      setError(
        err instanceof ApiError && err.code === 'insufficient_shelf_stock'
          ? t('shelf.move.insufficient')
          : t('common.error'),
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog title={t('shelf.move.title')} onClose={onClose}>
      {result ? (
        <div className="space-y-3 text-sm">
          <p>{t('shelf.move.done')}</p>
          <Link className="text-primary underline" to={`/pallets/${result.palletId}`}>
            {result.palletCode}
          </Link>
          <div className="flex justify-end">
            <button
              type="button"
              onClick={onClose}
              className="rounded border border-border px-3 py-1.5"
            >
              {t('common.close')}
            </button>
          </div>
        </div>
      ) : (
        <form onSubmit={(event) => void submit(event)} className="space-y-3 text-sm">
          <p>
            <span className="font-mono">{item.code}</span> — {item.nameLt} · {t('shelf.qty')}:{' '}
            {formatQty(item.shelfQty)}
          </p>
          <label className="block space-y-1">
            <span>{t('pallet.shelf.qty')}</span>
            <NumberInput
              integer
              min={1}
              max={item.shelfQty}
              value={qty}
              onChange={(value) => setQty(value ?? 0)}
              className="w-full rounded border border-border bg-background px-2 py-1"
            />
          </label>
          <label className="block space-y-1">
            <span>{t('shelf.move.target')}</span>
            <select
              value={target}
              onChange={(event) => setTarget(event.target.value)}
              className="w-full rounded border border-border bg-background px-2 py-1"
            >
              <option value="NEW">{t('shelf.move.new_pallet')}</option>
              {pallets.map((pallet) => (
                <option key={pallet.id} value={pallet.id}>
                  {pallet.code}
                </option>
              ))}
            </select>
          </label>
          {target === 'NEW' ? (
            <div className="grid grid-cols-3 gap-2">
              {(
                [
                  [t('location.row'), locationRow, setLocationRow],
                  [t('location.shelf'), locationShelf, setLocationShelf],
                  [t('location.position'), locationPosition, setLocationPosition],
                ] as const
              ).map(([label, value, setter]) => (
                <label key={label} className="block space-y-1">
                  <span>{label}</span>
                  <input
                    value={value}
                    onChange={(event) => setter(event.target.value)}
                    maxLength={8}
                    className="w-full rounded border border-border bg-background px-2 py-1"
                  />
                </label>
              ))}
            </div>
          ) : null}
          <label className="block space-y-1">
            <span>{t('pallet.shelf.reason')}</span>
            <input
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              maxLength={2000}
              required
              className="w-full rounded border border-border bg-background px-2 py-1"
            />
          </label>
          {error ? (
            <p role="alert" className="text-red-600">
              {error}
            </p>
          ) : null}
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded border border-border px-3 py-1.5"
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              disabled={!valid || busy}
              className="rounded bg-primary px-3 py-1.5 text-primary-foreground disabled:opacity-50"
            >
              {t('shelf.move.submit')}
            </button>
          </div>
        </form>
      )}
    </Dialog>
  );
}
